import mobile1 from "../assets/mobile1.webp";
import groupedBadgeImage from "../assets/grouped-badges.svg";
import { Helmet } from "react-helmet-async";

import userExperienceIcon from "../assets/user-eexperiencee.svg";
import userEngagementIcon from "../assets/user-fengagement.png";
import userInterfaceIcon from "../assets/user-finterface.png";
import agentImage from "../assets/agent-response.webp";
import { motion } from "framer-motion";

const MobileAppSection = () => {
  return (
    <>
      <Helmet>
        <title>
          Mobile App Development | iOS, Android & Cross-Platform by Lentera
        </title>
        <meta
          name="description"
          content="Lentera Technologies develops custom mobile apps for iOS, Android, and cross-platform frameworks, delivering scalable, engaging, and user-focused solutions."
        />
        <meta
          name="keywords"
          content="Custom mobile apps, iOS app development, Android app development, cross-platform apps, React Native, Flutter, enterprise mobile solutions, user experience apps.Custom mobile apps, iOS app development, Android app development, cross-platform apps, React Native, Flutter, enterprise mobile solutions, user experience apps."
        />
      </Helmet>

      {/* SECTION 2: Colored Cards */}

      <section className="w-full bg-[#FAFAFA] py-20 px-4 sm:px-6">
        <div className="w-full max-w-[1312px] mx-auto flex flex-col lg:flex-row items-center justify-between gap-10">
          {/* Left Column – 60% with animation */}
          <motion.div
            className="w-full lg:w-[60%]"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <h5 className="text-[#009c3c] text-sm font-semibold uppercase mb-3 tracking-wide">
              Mobile App Development
            </h5>

            <h2 className="text-[#1E1E1E] text-[40px] md:text-[48px] font-extrabold leading-tight mb-6">
              Transform your business <br className="hidden sm:block" /> with
              Cutting-Edge
            </h2>

            <p className="text-[#4A4A4A] text-base leading-relaxed max-w-[580px]">
              We focuses on creating software applications specifically designed
              to operate on mobile devices such as smartphones and tablets.
            </p>
          </motion.div>

          {/* Right Column – 40% with animation */}
          <motion.div
            className="w-full lg:w-[40%] flex justify-center"
            initial={{ opacity: 0, x: 60 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
          >
            <img
              src={mobile1}
              alt="Mobile App Showcase"
              className="w-454 max-w-[450px] h-auto object-contain"
            />
          </motion.div>
        </div>
      </section>

      <section className="w-full bg-[#121212] text-white font-poppins px-4 md:px-12 xl:px-20 py-16">
        <div className="max-w-screen-xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Comprehensive solutions tailored <br className="hidden sm:block" />
            to your business
          </h2>
          <div className="w-16 h-px bg-white/40 mx-auto mt-4 mb-12" />
        </div>

        <div className="max-w-screen-xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10">
          {/* === GREEN CARD === */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="rounded-[28px] p-8 text-[#1A1A1A] shadow-xl bg-[#EFFFCC] relative overflow-hidden"
            style={{
              backgroundImage: `
          radial-gradient(120px 80px at 10% 30%, #A5F642 0%, transparent 100%),
          radial-gradient(200px 150px at 50% 70%, #C6FA85 0%, transparent 100%),
          radial-gradient(180px 130px at 90% 50%, #D7FFA6 0%, transparent 100%)
        `,
              backgroundRepeat: "no-repeat",
              backgroundSize: "cover",
            }}
          >
            <h3 className="text-[22px] font-semibold mb-4 text-center relative z-10">
              Overview
            </h3>
            <p className="text-[16px] leading-relaxed relative z-10">
              It encompasses the entire process from conceptualization and
              design to testing and deployment, leveraging the capabilities of
              mobile platforms like iOS, Android, and cross-platform frameworks
              such as React Native and Flutter.
            </p>
          </motion.div>

          {/* === BLUE CARD === */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="rounded-[28px] p-8 text-[#1A1A1A] shadow-xl bg-[#E6FBFF] relative overflow-hidden"
            style={{
              backgroundImage: `
          radial-gradient(120px 80px at 10% 30%, #93E5FF 0%, transparent 100%),
          radial-gradient(200px 150px at 50% 70%, #C3F3FF 0%, transparent 100%),
          radial-gradient(180px 130px at 90% 50%, #E6FBFF 0%, transparent 100%)
        `,
              backgroundRepeat: "no-repeat",
              backgroundSize: "cover",
            }}
          >
            <h3 className="text-[22px] font-semibold text-center mb-4 relative z-10">
              Mobile Apps at <span className="font-bold">LENTERA</span>
            </h3>
            <p className="text-[16px] leading-relaxed relative z-10">
              Custom mobile app development at <strong>LENTERA</strong> tailor
              applications to specific business needs, ensuring they align
              perfectly with operational requirements and exceed user
              expectations. This approach enhances the app’s functionality and
              usability, driving engagement and satisfaction among users.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="w-full bg-white py-20 px-4 md:px-16 xl:px-28 font-poppins">
        <div className="max-w-[1440px] mx-auto flex flex-col lg:flex-row items-start justify-between gap-12">
          {/* LEFT SIDE – Badge Image */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="w-full lg:w-[60%]"
          >
            <h2 className="text-[40px] md:text-[48px] font-extrabold text-[#1C1C1C] leading-tight mb-4">
              Benefits of mobile app <br className="hidden md:block" />{" "}
              development
            </h2>
            <div className="w-[60px] h-[4px] bg-[#119D57] rounded-full mb-6"></div>
            <p className="text-[#4D4D4D] text-[16px] md:text-[18px] leading-[28px] mb-10">
              Discover the power of mobile app development to elevate your
              business.
            </p>
            <img
              src={groupedBadgeImage}
              alt="Grouped mobile app benefits badges"
              className="w-full max-w-[880px] h-auto object-contain"
            />
          </motion.div>

          {/* RIGHT SIDE – Agent Image */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="w-full lg:w-[40%] flex justify-center lg:justify-end"
          >
            <img
              src={agentImage}
              alt="Agent responding to a message"
              className="w-full max-w-[480px] h-auto object-contain"
            />
          </motion.div>
        </div>
      </section>

      <section className="bg-[#f9f9f9] py-20 px-4 font-poppins">
        <div className="max-w-6xl mx-auto text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Key features
          </h2>
          <p className="text-lg text-gray-600">
            Discover the game-changing key features that set you apart!
          </p>
        </div>

        <div className="max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 text-center">
          {[
            {
              icon: userExperienceIcon,
              title: "User experience",
              bg: "#ffe5e5",
              text: "By providing performance even in low-connectivity settings, these apps improve user experience & accessible.",
            },
            {
              icon: userEngagementIcon,
              title: "User engagement",
              bg: "#e8f4ff",
              text: "By providing experiences & optimizing information according to user behavior and preferences in real time.",
            },
            {
              icon: userInterfaceIcon,
              title: "User interface",
              bg: "#fff7d4",
              text: "Responsive apps deliver a consistent user experience across PCs, tablets, and smartphones.",
            },
          ].map((feature, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: i * 0.2 }}
              viewport={{ once: true }}
              className="bg-white rounded-3xl p-8 shadow-md hover:shadow-lg transition duration-300"
            >
              <div
                className="w-24 h-24 mx-auto mb-6 rounded-full flex items-center justify-center"
                style={{ backgroundColor: feature.bg }}
              >
                <img
                  src={feature.icon}
                  alt={feature.title}
                  className="w-10 h-10"
                />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                {feature.title}
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                {feature.text}
              </p>
            </motion.div>
          ))}
        </div>
      </section>
    </>
  );
};

export default MobileAppSection;
